import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { saveIotSetup } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import { useIotDeviceData, useAddIotServices, useGetIotServices } from '../../../../hooks/API/wizard.api';
import '../../../../styles/wizard.css';
import { IOT_INTERACTION_MODES } from '../../../../Config/Onboarding/constant';

const Step8IotInteractionSetup = () => {
  const navigate = useNavigate();
  const { iot, setIot, nextStep, previousStep, markStepCompleted, goToStep, project } = useOnboarding();
  
  // Fetch IOT device data
  const { data: deviceData, isLoading: isLoadingDevice, error: deviceError } = useIotDeviceData(project?.project_id);
  
  const [deviceId, setDeviceId] = useState(null);
  const [deviceAddress, setDeviceAddress] = useState(null);
  const [isFetchingServices, setIsFetchingServices] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [docbotIp, setDocbotIp] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const servicesTimeoutRef = useRef(null);
  const calibrationTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  
  // API Mutations
  const addIotServicesMutation = useAddIotServices();
  const { data: iotServices, isLoading: isLoadingServices, refetch: refetchServices } = useGetIotServices(deviceId);
  
  // State for PHYSICAL mode - Calibration
  const [calibrationStatus, setCalibrationStatus] = useState(iot.setup?.calibrationProfile ? 'completed' : 'pending');
  const [actuatorConfig, setActuatorConfig] = useState(iot.setup?.actuatorConfig || {
    force: 50,
    speed: 50,
    precision: 'medium'
  });
  
  const [isSaving, setIsSaving] = useState(false);

  // Effect: Extract device id and address from API response
  useEffect(() => {
    if (deviceData?.device?.id && deviceData?.device?.address) {
      setDeviceId(deviceData.device.id);
      setIot( ({ device_id: deviceData?.device?.id }));
      setDeviceAddress(deviceData.device.address);
    }
  }, [deviceData]);

  // Effect: Handle IOT_DISCOVER_SERVICES event listener
  useEffect(() => {
    if (!isFetchingServices || !deviceAddress) return;

    const handleDiscoveredServices = async (event) => {
      // Check for our custom source
      if (event.data?.source !== "ROBITEST_AGENT") return;

      console.log('🔧 IOT Services Discovery Response:', event.data);
    
      clearTimeout(servicesTimeoutRef.current);
      const payload = event.data.payload || event.data;
      if (event.data.success || payload.services) {
        try {
          // Prepare the payload for the API - sending all response data
          // const servicesPayload = {
          //   source: event.data.source || payload.source || 'ROBITEST_AGENT',
          //   type: event.data.type || payload.type || 'IOT_SERVICES_RESULTS',
          //   success: true,
          //   address: event.data.address || payload.address || deviceAddress,
          //   services: payload.services || event.data.services || []
          // };

          // Call API to save services
          await addIotServicesMutation.mutateAsync({
            deviceId: deviceId,
            "servicesPayload": payload
          });

          // After successful save, refetch services from backend
          await refetchServices();

          setIsFetchingServices(false);
          toast.success('IOT services discovered and saved successfully!');
        } catch (error) {
          console.error('Error saving IOT services:', error);
          setIsFetchingServices(false);
          toast.error('Failed to save IOT services');
        }
      } else {
        setIsFetchingServices(false);
        toast.error(event.data.error || 'Failed to discover IOT services.');
      }
    };

    window.addEventListener('message', handleDiscoveredServices);

    // Timeout after 5 minutes if no response
    servicesTimeoutRef.current = setTimeout(() => {
      setIsFetchingServices(false);
      toast.error('Service discovery timeout. Is the extension installed?');
    }, 300000);

    return () => {
      window.removeEventListener('message', handleDiscoveredServices);
      clearTimeout(servicesTimeoutRef.current);
    };
  }, [isFetchingServices, deviceAddress, deviceId, refetchServices, addIotServicesMutation]);

  // Effect: Consolidated Event Listener for all ROBITEST_AGENT events
  useEffect(() => {
    const handleRobitesteAgentMessage = (event) => {
      // Check for ROBITEST_AGENT source
      if (event.data?.source !== "ROBITEST_AGENT") return;

      const eventType = event.data?.type;

      console.log('📨 ROBITEST_AGENT Event:', { type: eventType, data: event.data });

      // Route based on event type
      switch (eventType) {
        case 'DOFBOT_CALIBRATE_RESULT':
          handleCalibrationResult(event.data);
          break;
        case 'DOFBOT_CONNECT_RESULT':
          handleConnectionResult(event.data);
          break;
        default:
          // Other event types can be handled here
          break;
      }
    };

    window.addEventListener('message', handleRobitesteAgentMessage);

    return () => {
      window.removeEventListener('message', handleRobitesteAgentMessage);
    };
  }, []);

  // Handler: Calibration Result
  const handleCalibrationResult = (data) => {
    clearTimeout(calibrationTimeoutRef.current);

    if (data.success) {
      setCalibrationStatus('completed');
      toast.success(data.message || 'Calibration completed successfully!');
      setIsCalibrating(false);
    } else {
      toast.error(data.message || data.error || 'Calibration failed. Please try again.');
      setIsCalibrating(false);
    }
  };

  // Handler: Connection Result
  const handleConnectionResult = (data) => {
    clearTimeout(connectionTimeoutRef.current);

    if (data.success) {
      toast.success(data.message || 'DOFBOT connected successfully!');
      setIsConnecting(false);
    } else {
      toast.error(data.message || data.error || 'Connection failed. Please check the IP address.');
      setIsConnecting(false);
    }
  };

  // Handler: Fetch IOT services - triggers extension to discover services
  const handleFetchIotServices = () => {
    if (!deviceAddress) {
      toast.error('Device address not found. Please check your device configuration.');
      return;
    }

    setIsFetchingServices(true);
    
    // Send postMessage to trigger service discovery
    window.postMessage(
      {
        source: "ROBITEST_CLOUD",
        action: "IOT_DISCOVER_SERVICES",
        address: deviceAddress
      },
      "*"
    );

    toast.info('Discovering IOT services... Please wait');

    // Timeout after 5 minutes if no response
    servicesTimeoutRef.current = setTimeout(() => {
      setIsFetchingServices(false);
      toast.error('Service discovery timeout. Is the extension installed?');
    }, 300000);
  };

  // Handler: Connect DOFBOT - triggers extension to connect to DOFBOT
  const handleConnectDofbot = () => {
    if (!docbotIp.trim()) {
      toast.error('Please enter the DOFBOT IP address');
      return;
    }

    setIsConnecting(true);
    
    // Send postMessage to trigger connection
    window.postMessage(
      {
        source: "ROBITEST_CLOUD",
        action: "DOFBOT_CONNECT_REQUEST",
        payload: { ip_address: docbotIp }
      },
      "*"
    );

    toast.info('Connecting to DOFBOT... Please wait');

    // Timeout after 5 minutes if no response
    connectionTimeoutRef.current = setTimeout(() => {
      setIsConnecting(false);
      toast.error('Connection timeout. Is the hardware online?');
    }, 300000);
  };

  // Render: IOT Services Discovery Section with Accordion
  const renderIotServicesDiscovery = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">
        Section 1: Discover & Manage IOT Services
      </h3>

      {/* Device Info */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">
              Device: <span className="font-mono text-blue-700">{deviceData?.device?.name || 'Loading...'}</span>
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Address: <span className="font-mono">{deviceAddress || 'Loading...'}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Service Discovery Button */}
      <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mb-6">
        <div className="text-center">
          <div className="mb-4 text-5xl">🔍</div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {iotServices && iotServices.length > 0 ? 'Refetch Services' : 'Ready to Discover Services'}
          </h4>
          <p className="text-sm text-gray-600 mb-6 max-w-sm">
            {iotServices && iotServices.length > 0
              ? 'Click the button to scan for latest IOT device services and capabilities'
              : 'Click the button below to scan your IOT device and discover available services and capabilities'
            }
          </p>
          <button
            onClick={handleFetchIotServices}
            disabled={isFetchingServices || isLoadingDevice || !deviceAddress}
            className={`px-6 py-3 font-semibold rounded-lg transition-all duration-200 ${
              isFetchingServices || !deviceAddress
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
            }`}
          >
            {isFetchingServices ? (
              <>
                <span className="inline-block animate-spin mr-2">⚙️</span>
                Discovering Services...
              </>
            ) : (
              <>
                🔧 Fetch IOT Services
              </>
            )}
          </button>
        </div>
      </div>

      {/* Discovered Services - Accordion */}
      {iotServices && iotServices.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Discovered Services ({iotServices.length})
          </h4>
          
          <div className="space-y-2">
            {iotServices.map((service) => (
              <div key={service.service_id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                {/* Accordion Header */}
                <button
                  onClick={() => setExpandedService(expandedService === service.service_id ? null : service.service_id)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className={`transform transition-transform ${expandedService === service.service_id ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                    <div>
                      <p className="font-semibold text-gray-900">{service.description}</p>
                      <p className="text-xs text-gray-500 font-mono mt-1">{service.service_uuid}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {service.characteristics?.length || 0} characteristics
                  </span>
                </button>

                {/* Accordion Content - Characteristics Table */}
                {expandedService === service.service_id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-6">
                    {service.characteristics && service.characteristics.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-300 bg-white">
                              <th className="px-4 py-3 text-left font-semibold text-gray-900">Characteristic</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900">UUID</th>
                              <th className="px-4 py-3 text-center font-semibold text-gray-900">Read</th>
                              <th className="px-4 py-3 text-center font-semibold text-gray-900">Write</th>
                              <th className="px-4 py-3 text-center font-semibold text-gray-900">Notify</th>
                            </tr>
                          </thead>
                          <tbody>
                            {service.characteristics.map((char, index) => (
                              <tr key={`${service.service_id}-${char.id || index}`} className="border-b border-gray-200 hover:bg-white transition-colors">
                                <td className="px-4 py-3 text-gray-900">
                                  <div className="font-medium">{char.description}</div>
                                  {char.properties && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {Array.isArray(char.properties) 
                                        ? char.properties.join(', ') 
                                        : char.properties
                                      }
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                  {char.char_uuid}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {char.can_read ? (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                                      <span className="text-green-600 font-bold">✓</span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full">
                                      <span className="text-gray-400">-</span>
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {char.can_write ? (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full">
                                      <span className="text-blue-600 font-bold">✓</span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full">
                                      <span className="text-gray-400">-</span>
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {char.can_notify ? (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 rounded-full">
                                      <span className="text-purple-600 font-bold">✓</span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full">
                                      <span className="text-gray-400">-</span>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No characteristics available</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoadingServices && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
          <span className="inline-block animate-spin mr-2">⚙️</span>
          <span className="text-blue-900">Loading services...</span>
        </div>
      )}
    </div>
  );

  // Render: Calibration Section (Section 2)
  const renderCalibrationSection = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">
        Section 2: Calibrate Physical Actuators
      </h3>

      {/* Actuator Configuration */}
      <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-6">
          Docbot IP
        </h4>
        
        <div className="space-y-6">
          {/* DOFBOT IP Input and Connect Button */}
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter the DOFBOT IP (e.g., 192.168.1.15)"
              value={docbotIp}
              onChange={(e) => setDocbotIp(e.target.value)}
              disabled={isConnecting}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-200 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleConnectDofbot}
              disabled={isConnecting || !docbotIp.trim()}
              className={`px-6 py-3 font-semibold rounded-lg transition-all duration-200 whitespace-nowrap ${
                isConnecting || !docbotIp.trim()
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
              }`}
            >
              {isConnecting ? (
                <>
                  <span className="inline-block animate-spin mr-2">⚙️</span>
                  Connecting...
                </>
              ) : (
                <>
                  🔗 Connect
                </>
              )}
            </button>
          </div>
          
          {/* Connection Status Info */}
          <p className="text-xs text-gray-600">
            Enter the IP address of your DOFBOT and click Connect to establish connection
          </p>
          {/* Force slider */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Force: <span className="font-bold text-blue-600">{actuatorConfig.force}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={actuatorConfig.force}
              onChange={(e) => setActuatorConfig({ ...actuatorConfig, force: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <p className="text-xs text-gray-600 mt-2">Pressure applied during physical interaction</p>
          </div> */}

          {/* Speed slider */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Speed: <span className="font-bold text-blue-600">{actuatorConfig.speed}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={actuatorConfig.speed}
              onChange={(e) => setActuatorConfig({ ...actuatorConfig, speed: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <p className="text-xs text-gray-600 mt-2">Speed of actuator movement</p>
          </div> */}

          {/* Precision */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Precision
            </label>
            <select
              value={actuatorConfig.precision}
              onChange={(e) => setActuatorConfig({ ...actuatorConfig, precision: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Low (±5mm)</option>
              <option value="medium">Medium (±2mm)</option>
              <option value="high">High (±0.5mm)</option>
            </select>
            <p className="text-xs text-gray-600 mt-2">Position accuracy for physical actions</p>
          </div> */}
        </div>
      </div>

      {/* Calibration Status */}
      <div className={`p-6 rounded-lg border-2 text-center ${
        calibrationStatus === 'completed'
          ? 'bg-green-50 border-green-200'
          : 'bg-gray-50 border-gray-300'
      }`}>
        {calibrationStatus === 'pending' ? (
          <>
            <div className="text-5xl mb-4">🎯</div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Calibration Required
            </h4>
            <p className="text-sm text-gray-600 mb-6">
              Run a calibration test to verify actuator positioning and force
            </p>
            <button
              onClick={handleRunCalibration}
              disabled={isCalibrating}
              className={`px-6 py-3 font-semibold rounded-lg transition-all duration-200 ${
                isCalibrating
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
              }`}
            >
              {isCalibrating ? (
                <>
                  <span className="inline-block animate-spin mr-2">⚙️</span>
                  Calibrating...
                </>
              ) : (
                <>
                  🔧 Run Calibration Test
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="text-5xl mb-4">✓</div>
            <h4 className="text-lg font-semibold text-green-600 mb-2">
              Calibration Complete
            </h4>
            <p className="text-sm text-gray-600 m-0">
              Actuators are calibrated and ready for testing
            </p>
          </>
        )}
      </div>
    </div>
  );

  // PHYSICAL mode: Run calibration
  const handleRunCalibration = () => {
    toast.info('Starting calibration... Please wait');
    setIsCalibrating(true);
    
    // Send postMessage to trigger calibration
    window.postMessage(
      {
        source: "ROBITEST_CLOUD",
        action: "DOFBOT_CALIBRATE_REQUEST"
      },
      "*"
    );

    // Timeout after 5 minutes if no response
    calibrationTimeoutRef.current = setTimeout(() => {
      setIsCalibrating(false);
      toast.error('Calibration timeout. Is the hardware connected?');
    }, 300000);
  };

  const canContinue = () => {
    const mode = iot.interactionMode;
    
    // PROTOCOL: Only service discovery is needed (user can continue after discovering)
    if (mode === IOT_INTERACTION_MODES.PROTOCOL) {
      return iotServices && iotServices.length > 0;
    } 
    // PHYSICAL: Only calibration is needed
    else if (mode === IOT_INTERACTION_MODES.PHYSICAL) {
      return calibrationStatus === 'completed';
    } 
    // HYBRID: Both calibration and service discovery are needed
    else if (mode === IOT_INTERACTION_MODES.HYBRID) {
      return calibrationStatus === 'completed' && iotServices && iotServices.length > 0;
    }
    
    return false;
  };

  const handleContinue = async () => {
    if (!canContinue()) {
      toast.error('Please complete the required setup steps');
      return;
    }

    setIsSaving(true);
    try {
      const setupData = {
        interaction_mode: iot.interactionMode,
        discovered_services: iotServices || undefined,
        calibration_profile: calibrationStatus === 'completed' ? actuatorConfig : undefined,
        actuator_config: actuatorConfig
      };

      const response = await saveIotSetup(setupData);
      
      if (response.success) {
        setIot({
          ...iot,
          setup: {
            discoveredServices: iotServices,
            calibrationProfile: calibrationStatus === 'completed' ? actuatorConfig : null,
            actuatorConfig
          }
        });
        markStepCompleted(8);
        goToStep(9);
        toast.success('Setup saved successfully');
        navigate('/app/onboarding?step=9');
      }
    } catch (error) {
      console.error('Error saving setup:', error);
      toast.error('Failed to save setup');
    } finally {
      setIsSaving(false);
    }
  };

  const renderProtocolSetup = () => (
    <div className="space-y-8">
      {/* Section 1: Discover IOT Services */}
      {renderIotServicesDiscovery()}
    </div>
  );

  const renderPhysicalSetup = () => (
    <div className="space-y-8">
      {/* Section 2: Calibrate Physical Actuators */}
      {renderCalibrationSection()}
    </div>
  );

  const renderHybridSetup = () => (
    <div className="space-y-8">
      {/* Section 1: Discover IOT Services */}
      <div className="p-6 bg-white rounded-lg border-2 border-blue-100">
        {renderIotServicesDiscovery()}
      </div>

      {/* Section 2: Calibrate Physical Actuators */}
      <div className="p-6 bg-white rounded-lg border-2 border-yellow-100">
        {renderCalibrationSection()}
      </div>
    </div>
  );

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={8} completedSteps={[1, 2, 3, 4, 5, 6, 7]} />
        
        <WizardCard
          title={`Interaction Setup - ${iot.interactionMode} Mode`}
          subtitle={
            iot.interactionMode === IOT_INTERACTION_MODES.PROTOCOL ? 'Configure protocol commands and validations' :
            iot.interactionMode === IOT_INTERACTION_MODES.PHYSICAL ? 'Calibrate physical actuators' :
            'Configure both protocol and physical setup'
          }
        >
          {iot.interactionMode === IOT_INTERACTION_MODES.PROTOCOL && renderProtocolSetup()}
          {iot.interactionMode === IOT_INTERACTION_MODES.PHYSICAL && renderPhysicalSetup()}
          {iot.interactionMode === IOT_INTERACTION_MODES.HYBRID && renderHybridSetup()}

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=7');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextLoading={isSaving}
            nextDisabled={!canContinue()}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step8IotInteractionSetup;